<template>
    <div>

        <!-- Barre admin (visible uniquement par les utilisateurs avec rules === null) -->
        <div v-if="isAdmin" class="doc-admin-bar">
            <template v-if="!editing">
                <button @click="startEdit" class="btn-edit">✏️ Modifier</button>
            </template>
            <template v-else>
                <input v-model="draftTitle" type="text" class="tab-title-input" placeholder="Nom de l'onglet" />
                <template v-if="draftType !== 'faq'">
                    <button @click="togglePreview" class="btn-preview" :disabled="editingHtml">
                        {{ previewing ? '📝 Éditer' : '👁 Prévisualiser' }}
                    </button>
                    <button @click="toggleHtml" class="btn-html" :class="{ active: editingHtml }">
                        {{ editingHtml ? '🖊 Visuel' : '&lt;/&gt; HTML' }}
                    </button>
                </template>
                <button @click="saveContent" class="btn-save">💾 Sauvegarder</button>
                <button @click="cancelEdit" class="btn-cancel">✕ Annuler</button>
            </template>
        </div>

        <!-- Onglets : chaque onglet correspond à une page -->
        <div class="doc-tabs">
            <button
                v-for="(page, idx) in pages"
                :key="idx"
                class="doc-tab"
                :class="{ active: idx === activeTab }"
                @click="selectTab(idx)"
            >
                <span
                    v-if="isAdmin && editing && idx > 0"
                    class="tab-move"
                    title="Déplacer à gauche"
                    @click.stop="moveTab(idx, -1)"
                >◀</span>
                <span class="tab-label">{{ page.title }}</span>
                <span
                    v-if="isAdmin && editing && idx < pages.length - 1"
                    class="tab-move"
                    title="Déplacer à droite"
                    @click.stop="moveTab(idx, 1)"
                >▶</span>
                <span
                    v-if="isAdmin && editing && pages.length > 1"
                    class="tab-remove"
                    title="Supprimer cet onglet"
                    @click.stop="removePage(idx)"
                >✕</span>
            </button>
            <button v-if="isAdmin && editing" class="doc-tab doc-tab-add" @click="openAddPageModal">+ Onglet</button>
        </div>

        <!-- Modal de création d'onglet -->
        <div v-if="showAddPageModal" class="modal-overlay" @click.self="closeAddPageModal">
            <div class="modal-box">
                <h3>Nouvel onglet</h3>
                <label class="modal-field">
                    <span>Nom de l'onglet</span>
                    <input v-model="newPageTitle" type="text" placeholder="Nom de l'onglet" @keyup.enter="confirmAddPage" />
                </label>
                <label class="modal-field">
                    <span>Type de page</span>
                    <select v-model="newPageType">
                        <option value="simple">Page simple</option>
                        <option value="faq">FAQ (questions/réponses)</option>
                    </select>
                </label>
                <div class="modal-actions">
                    <button class="btn-save" @click="confirmAddPage">Créer</button>
                    <button class="btn-cancel" @click="closeAddPageModal">Annuler</button>
                </div>
            </div>
        </div>

        <div class="doc-layout">

            <!-- Contenu principal -->
            <div class="doc-content">
                <!-- Mode visualisation -->
                <template v-if="!editing">
                    <div v-if="currentPage && currentPage.type === 'faq'" class="faq-list">
                        <div class="faq-block" v-for="(block, idx) in currentPage.blocks" :key="idx">
                            <button type="button" class="faq-question" @click="toggleFaq(idx)">
                                <span class="faq-icon">{{ openFaqIndexes[idx] ? '−' : '+' }}</span>
                                {{ block.question }}
                            </button>
                            <div v-show="openFaqIndexes[idx]" class="faq-answer" v-html="block.answer"></div>
                        </div>
                    </div>
                    <div v-else class="doc-view" v-html="renderedHtml"></div>
                </template>
                <!-- Mode édition -->
                <div v-else>
                    <div v-if="draftType === 'faq'" class="faq-edit-list">
                        <div class="faq-edit-block" v-for="(block, idx) in draftBlocks" :key="idx">
                            <div class="faq-edit-row">
                                <span v-if="idx > 0" class="tab-move" title="Monter" @click="moveBlock(idx, -1)">▲</span>
                                <input v-model="block.question" type="text" class="faq-question-input" placeholder="Question" />
                                <span v-if="idx < draftBlocks.length - 1" class="tab-move" title="Descendre" @click="moveBlock(idx, 1)">▼</span>
                                <button type="button" class="btn-html-small" :class="{ active: block.htmlMode }" @click="toggleBlockHtml(idx)">
                                    {{ block.htmlMode ? '🖊 Visuel' : '&lt;/&gt; HTML' }}
                                </button>
                                <span class="tab-remove" title="Supprimer cette question" @click="removeBlock(idx)">✕</span>
                            </div>
                            <textarea v-if="block.htmlMode" v-model="block.answer" class="html-source faq-answer-html" spellcheck="false"></textarea>
                            <vue-editor v-else :editorToolbar="toolbar" v-model="block.answer" class="faq-answer-editor"></vue-editor>
                        </div>
                        <button type="button" class="btn-add-block" @click="addBlock">+ Question</button>
                    </div>
                    <template v-else>
                        <div v-if="previewing" v-html="renderedHtml" class="doc-view doc-preview"></div>
                        <textarea v-else-if="editingHtml" v-model="draft" class="html-source" spellcheck="false"></textarea>
                        <vue-editor ref="quillEditor" v-else :editorToolbar="toolbar" v-model="draft"></vue-editor>
                    </template>
                </div>
            </div>

        </div>

    </div>
</template>

<script>

    import { VueEditor } from "vue2-editor";
    import configService from "../../services/config";
    import store from "../../store";

    export default {
        components: { VueEditor },
        data() {
            return {
                pages: [],
                pagesBackup: null,
                activeTab: 0,
                draft: "",
                draftTitle: "",
                draftType: "simple",
                draftBlocks: [],
                openFaqIndexes: {},
                showAddPageModal: false,
                newPageTitle: "",
                newPageType: "simple",
                editing: false,
                previewing: false,
                editingHtml: false,
                toolbar: [
                    ["bold", "italic", "underline", "strike"],
                    [{ header: [1, 2, 3, false] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image"],
                    ["clean"]
                ]
            };
        },
        computed: {
            isAdmin() {
                return store.state.user && store.state.user.rules === null;
            },
            currentPage() {
                return this.pages[this.activeTab] || null;
            },
            currentHtml() {
                if (this.editing) return this.draft;
                return this.currentPage ? this.currentPage.content_html : "";
            },
            renderedHtml() {
                return this.injectIds(this.currentHtml);
            }
        },
        watch: {
            activeTab() {
                this.openFaqIndexes = {};
            }
        },
        methods: {
            slugify(text) {
                return text
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^a-z0-9\s-]/g, "")
                    .trim()
                    .replace(/\s+/g, "-");
            },
            injectIds(html) {
                if (!html) return "";
                return html.replace(/<(h[123])([^>]*)>([\s\S]*?)<\/\1>/gi, (match, tag, attrs, content) => {
                    const text = content.replace(/<[^>]+>/g, "");
                    const id = this.slugify(text);
                    const cleanAttrs = attrs.replace(/\s*id="[^"]*"/g, "");
                    return `<${tag}${cleanAttrs} id="${id}">${content}</${tag}>`;
                });
            },
            // Enregistre les modifications en cours (draft/draftTitle) dans le tableau pages,
            // sans les persister en base. Utilisé avant de changer d'onglet ou de sauvegarder.
            commitDraft() {
                if (!this.editing || !this.pages[this.activeTab]) return;
                const current = this.pages[this.activeTab];
                const title = this.draftTitle || current.title;
                if (this.draftType === "faq") {
                    const blocks = this.draftBlocks.map(b => ({ question: b.question, answer: this.injectIds(b.answer) }));
                    this.pages.splice(this.activeTab, 1, { title, type: "faq", blocks });
                } else {
                    const html = this.injectIds(this.draft);
                    this.pages.splice(this.activeTab, 1, { title, type: "simple", content_html: html });
                }
            },
            loadDraftFromPage() {
                const page = this.pages[this.activeTab];
                this.draftTitle = page ? page.title : "";
                this.draftType = page ? (page.type || "simple") : "simple";
                if (this.draftType === "faq") {
                    const blocks = page && Array.isArray(page.blocks) ? JSON.parse(JSON.stringify(page.blocks)) : [];
                    this.draftBlocks = blocks.map(b => ({ question: b.question, answer: b.answer, htmlMode: false }));
                    this.draft = "";
                } else {
                    this.draft = page ? page.content_html : "";
                    this.draftBlocks = [];
                }
                this.previewing = false;
                this.editingHtml = false;
            },
            addBlock() {
                this.draftBlocks.push({ question: "", answer: "", htmlMode: false });
            },
            removeBlock(idx) {
                if (!window.confirm("Supprimer cette question ?")) return;
                this.draftBlocks.splice(idx, 1);
            },
            moveBlock(idx, delta) {
                const newIdx = idx + delta;
                if (newIdx < 0 || newIdx >= this.draftBlocks.length) return;
                const arr = this.draftBlocks.slice();
                [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
                this.draftBlocks = arr;
            },
            toggleBlockHtml(idx) {
                const block = this.draftBlocks[idx];
                if (!block) return;
                if (block.htmlMode) {
                    block.answer = this.cleanForQuill(block.answer);
                    this.$nextTick(() => {
                        this.$set(block, "htmlMode", false);
                    });
                } else {
                    block.answer = this.formatHtml(block.answer);
                    this.$set(block, "htmlMode", true);
                }
            },
            toggleFaq(idx) {
                this.$set(this.openFaqIndexes, idx, !this.openFaqIndexes[idx]);
            },
            selectTab(idx) {
                if (idx === this.activeTab) return;
                if (this.editing) this.commitDraft();
                this.activeTab = idx;
                if (this.editing) this.loadDraftFromPage();
            },
            openAddPageModal() {
                this.newPageTitle = "";
                this.newPageType = "simple";
                this.showAddPageModal = true;
            },
            closeAddPageModal() {
                this.showAddPageModal = false;
            },
            confirmAddPage() {
                const title = this.newPageTitle.trim();
                if (!title) return;
                if (this.editing) this.commitDraft();
                const newPage = this.newPageType === "faq" ? { title, type: "faq", blocks: [] } : { title, type: "simple", content_html: "" };
                this.pages.push(newPage);
                this.activeTab = this.pages.length - 1;
                if (this.editing) this.loadDraftFromPage();
                this.persistPages();
                this.showAddPageModal = false;
            },
            removePage(idx) {
                if (this.pages.length <= 1) return;
                if (!window.confirm(`Supprimer l'onglet "${this.pages[idx].title}" ?`)) return;
                this.pages.splice(idx, 1);
                if (this.activeTab >= this.pages.length) this.activeTab = this.pages.length - 1;
                this.persistPages();
            },
            moveTab(idx, delta) {
                const newIdx = idx + delta;
                if (newIdx < 0 || newIdx >= this.pages.length) return;
                const arr = this.pages.slice();
                [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
                this.pages = arr;
                if (this.activeTab === idx) this.activeTab = newIdx;
                else if (this.activeTab === newIdx) this.activeTab = idx;
                this.persistPages();
            },
            startEdit() {
                if (!this.currentPage) return;
                this.pagesBackup = JSON.parse(JSON.stringify(this.pages));
                this.editing = true;
                this.loadDraftFromPage();
            },
            cancelEdit() {
                if (this.pagesBackup) {
                    this.pages = this.pagesBackup;
                    this.pagesBackup = null;
                }
                this.editing = false;
                this.previewing = false;
                this.editingHtml = false;
                this.draft = "";
                this.draftTitle = "";
                this.draftType = "simple";
                this.draftBlocks = [];
            },
            togglePreview() {
                this.previewing = !this.previewing;
            },
            formatHtml(html) {
                if (!html) return "";
                const BLOCK = "h1|h2|h3|h4|h5|h6|p|div|ul|ol|li|blockquote|pre|table|tr|td|th|thead|tbody|br";
                return html
                    .replace(new RegExp(`<((?:${BLOCK})[^>]*)>`, "gi"), "\n<$1>")
                    .replace(new RegExp(`<\\/(${BLOCK})>`, "gi"), "</$1>\n")
                    .replace(/\n{3,}/g, "\n\n")
                    .trim();
            },
            // Utilise le DOM du navigateur pour nettoyer le HTML avant de le donner à Quill :
            // supprime les nœuds texte qui ne contiennent que des espaces/retours à la ligne
            // entre les éléments block, ce qui évite les <p><br></p> parasites.
            cleanForQuill(html) {
                if (!html) return "";
                const div = document.createElement("div");
                div.innerHTML = html;
                const walk = (node) => {
                    Array.from(node.childNodes).forEach(child => {
                        if (child.nodeType === 3 && child.textContent.trim() === "") {
                            node.removeChild(child);
                        } else if (child.nodeType === 1) {
                            walk(child);
                        }
                    });
                };
                walk(div);
                return div.innerHTML;
            },
            toggleHtml() {
                this.previewing = false;
                if (this.editingHtml) {
                    // Repasse en visuel : nettoie via le DOM puis attend le tick suivant
                    // pour que Quill monte après que draft soit à jour
                    this.draft = this.cleanForQuill(this.draft);
                    this.$nextTick(() => {
                        this.editingHtml = false;
                    });
                } else {
                    // Passe en HTML : indente pour lisibilité
                    this.draft = this.formatHtml(this.draft);
                    this.editingHtml = true;
                }
            },
            async loadContent() {
                try {
                    const res = await configService.list("page");
                    const conf = (res.configs || []).find(c => c.name === "documentation_client");
                    if (conf) {
                        const data = await configService.load(conf.uid);
                        const configData = (data.config && data.config.data) || {};
                        if (Array.isArray(configData.pages) && configData.pages.length) {
                            this.pages = configData.pages.map(p => ({ type: "simple", content_html: "", blocks: [], ...p }));
                        } else if (configData.content_html) {
                            // Migration depuis l'ancien format (une seule page)
                            this.pages = [{ title: "Documentation", type: "simple", content_html: configData.content_html }];
                        }
                    }
                } catch (e) {
                    console.error("Erreur chargement documentation", e);
                }
                if (!this.pages.length) {
                    this.pages = [{ title: "Documentation", type: "simple", content_html: "" }];
                }
                this.activeTab = 0;
            },
            _attachPasteScrollFix() {
                const editor = this.$refs.quillEditor;
                if (!editor || !editor.quill) return;
                const root = editor.quill.root;
                if (root._pasteScrollBound) return;
                root._pasteScrollBound = true;
                // Phase de capture pour intercepter avant Quill
                root.addEventListener('paste', () => {
                    const scrollY = window.scrollY;
                    // Double RAF : Quill traite le paste en plusieurs passes
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            window.scrollTo(0, scrollY);
                        });
                    });
                }, true);
            },
            async persistPages() {
                try {
                    // scope "shared" → shared='readonly' en base → visible par tous les utilisateurs
                    await configService.save("documentation_client", 1, "page", { pages: this.pages }, "shared");
                } catch (e) {
                    console.error("Erreur sauvegarde documentation", e);
                }
            },
            async saveContent() {
                try {
                    this.commitDraft();
                    await this.persistPages();
                    this.pagesBackup = null;
                    this.editing = false;
                    this.previewing = false;
                    this.draft = "";
                    this.draftTitle = "";
                    this.draftType = "simple";
                    this.draftBlocks = [];
                } catch (e) {
                    console.error("Erreur sauvegarde documentation", e);
                }
            }
        },
        mounted() {
            this.loadContent();
        },
        updated() {
            this._attachPasteScrollFix();
        }
    };

</script>

<style scoped>
    .doc-layout {
        display: flex;
        align-items: flex-start;
    }

    .doc-admin-bar {
        display: flex;
        gap: 0.5em;
        padding: 0.5em 1em;
        background: #f8f9fa;
        border-bottom: 1px solid #ddd;
        margin-bottom: 1em;
    }

    .doc-admin-bar button {
        padding: 0.3em 0.8em;
        border: 1px solid #ccc;
        border-radius: 3px;
        cursor: pointer;
        font-size: 0.9em;
    }

    .btn-save { background: #28a745; color: white; border-color: #28a745; }
    .btn-cancel { background: #dc3545; color: white; border-color: #dc3545; }
    .btn-edit { background: #007bff; color: white; border-color: #007bff; }
    .btn-preview { background: #6c757d; color: white; border-color: #6c757d; }
    .btn-html { background: #fff; color: #333; border-color: #aaa; font-family: monospace; }
    .btn-html.active { background: #333; color: #fff; border-color: #333; }

    .tab-title-input {
        padding: 0.3em 0.6em;
        border: 1px solid #ccc;
        border-radius: 3px;
        font-size: 0.9em;
    }

    .doc-tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 0.3em;
        border-bottom: 1px solid #ddd;
        margin-bottom: 1em;
    }

    .doc-tab {
        display: flex;
        align-items: center;
        gap: 0.5em;
        padding: 0.5em 1em;
        background: #f0f0f0;
        color: #333;
        border: 1px solid #ddd;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
        cursor: pointer;
        font-size: 0.95em;
    }

    .doc-tab:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    .doc-tab.active {
        background: #fff;
        color: #000;
        font-weight: bold;
        border-color: #ddd;
        box-shadow: 0 -2px 0 #007bff inset;
    }

    .doc-tab-add {
        background: transparent;
        border-style: dashed;
        color: #555;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal-box {
        background: #fff;
        border-radius: 6px;
        padding: 1.5em;
        width: 100%;
        max-width: 380px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    }

    .modal-box h3 {
        margin: 0 0 1em;
    }

    .modal-field {
        display: flex;
        flex-direction: column;
        gap: 0.3em;
        margin-bottom: 1em;
        font-size: 0.9em;
        color: #333;
    }

    .modal-field input,
    .modal-field select {
        padding: 0.5em 0.6em;
        border: 1px solid #ccc;
        border-radius: 3px;
        font-size: 1em;
    }

    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5em;
    }

    .tab-remove {
        color: #999;
        font-size: 0.85em;
        line-height: 1;
    }

    .tab-remove:hover {
        color: #dc3545;
    }

    .tab-move {
        color: #999;
        font-size: 0.75em;
        line-height: 1;
    }

    .tab-move:hover {
        color: #007bff;
    }

    .doc-content {
        flex: 1;
        min-width: 0;
    }

    .faq-list {
        display: flex;
        flex-direction: column;
        gap: 0.6em;
    }

    .faq-block {
        border: 1px solid #ddd;
        border-radius: 4px;
        overflow: hidden;
    }

    .faq-question {
        display: flex;
        align-items: center;
        gap: 0.6em;
        width: 100%;
        text-align: left;
        padding: 0.7em 1em;
        background: #f8f9fa;
        border: none;
        cursor: pointer;
        font-size: 1.1em;
        font-weight: bold;
        color: #222;
    }

    .faq-question:hover {
        background: #eee;
    }

    .faq-icon {
        display: inline-block;
        width: 1.2em;
        text-align: center;
        color: #007bff;
    }

    .faq-answer {
        padding: 0.9em 1.2em;
        font-size: 1.05em;
        border-top: 1px solid #eee;
    }

    .faq-edit-list {
        display: flex;
        flex-direction: column;
        gap: 0.8em;
    }

    .faq-edit-block {
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 0.7em;
        background: #fafafa;
    }

    .faq-edit-row {
        display: flex;
        align-items: center;
        gap: 0.5em;
        margin-bottom: 0.5em;
        flex-wrap: wrap;
    }

    .faq-question-input {
        flex: 1;
        min-width: 150px;
        padding: 0.4em 0.6em;
        border: 1px solid #ccc;
        border-radius: 3px;
        font-size: 1em;
        font-weight: bold;
    }

    .btn-html-small {
        padding: 0.3em 0.7em;
        border: 1px solid #aaa;
        border-radius: 3px;
        background: #fff;
        color: #333;
        font-family: monospace;
        font-size: 0.8em;
        cursor: pointer;
    }

    .btn-html-small.active {
        background: #333;
        color: #fff;
        border-color: #333;
    }

    .faq-answer-html {
        width: 100%;
        min-height: 120px;
    }

    .faq-answer-editor {
        border: 1px solid #ccc;
        border-radius: 3px;
        background: #fff;
    }

    ::v-deep .faq-answer-editor .ql-toolbar.ql-snow {
        position: static;
        border: none;
        border-bottom: 1px solid #ccc;
        border-radius: 3px 3px 0 0;
    }

    ::v-deep .faq-answer-editor .ql-container.ql-snow {
        border: none;
        min-height: 120px;
    }

    .btn-add-block {
        align-self: flex-start;
        padding: 0.4em 1em;
        border: 1px dashed #007bff;
        background: transparent;
        color: #007bff;
        border-radius: 3px;
        cursor: pointer;
    }

    .doc-content > div {
        padding-top: 1em;
        margin-bottom: 2em;
    }

    img {
        margin-bottom: 1em;
        max-width: 100%;
    }

    ::v-deep .ql-toolbar.ql-snow {
        position: sticky;
        top: 0;
        z-index: 100;
        background: #fff;
        border-bottom: 2px solid #ccc;
    }

    ::v-deep .doc-view p,
    ::v-deep .doc-view h1,
    ::v-deep .doc-view h2,
    ::v-deep .doc-view h3,
    ::v-deep .doc-view h4,
    ::v-deep .doc-view h5,
    ::v-deep .doc-view h6,
    ::v-deep .doc-view ol,
    ::v-deep .doc-view ul,
    ::v-deep .doc-view pre,
    ::v-deep .doc-view blockquote {
        margin: 0;
        padding: 0;
    }

    ::v-deep .doc-view p {
        font-size: 1.2em;
    }

    .html-source {
        width: 100%;
        min-height: 500px;
        font-family: 'Courier New', Courier, monospace;
        font-size: 0.85em;
        line-height: 1.5;
        padding: 0.8em;
        border: 1px solid #ccc;
        border-radius: 3px;
        resize: vertical;
        background: #1e1e1e;
        color: #d4d4d4;
        tab-size: 2;
        white-space: pre;
        overflow-wrap: normal;
        overflow-x: auto;
    }
</style>
